import { StoreName } from '@coach-bot/data-access/shared/enums';
import { CopilotNotificationConfiguration, CopilotScript, DashboardMessage, UserDetails } from '@coach-bot/data-access/shared/models';
import { AbstractStore } from '@coach-bot/data-access/shared/stores/abstract/abstract.store';
import { DataType } from '@shared/extension/enums';
import { ContextMenuActionData } from '@shared/extension/interfaces';
import { LocalStorageService } from '../services';

export interface CommonState {
  backendUrl: string;
  contextMenuActions: ContextMenuActionData[];
  copilotNotificationConfigurations: CopilotNotificationConfiguration[];
  copilotScripts: CopilotScript[];
  isExtensionPopupOpened: boolean;
  isSignedIn: boolean;
  lastSignInTime: number | null;
  latestDashboardMessage: DashboardMessage | null;
  profileEmail: string;
  studentId: string | null;
  userDetails: UserDetails | null;
  userDetailsLoaded: boolean;
  userId: string | null;
}

export class CommonStore extends AbstractStore<CommonState> {
  // Main selectors
  readonly backendUrl$ = this.select('backendUrl');
  readonly contextMenuActions$ = this.select('contextMenuActions');
  readonly copilotNotificationConfigurations$ = this.select('copilotNotificationConfigurations');
  readonly copilotScripts$ = this.select('copilotScripts');
  readonly isExtensionPopupOpened$ = this.select('isExtensionPopupOpened');
  readonly isSignedIn$ = this.select('isSignedIn');
  readonly lastSignInTime$ = this.select('lastSignInTime');
  readonly latestDashboardMessage$ = this.select('latestDashboardMessage');
  readonly profileEmail$ = this.select('profileEmail');
  readonly studentId$ = this.select('studentId');
  readonly userDetails$ = this.select('userDetails');
  readonly userDetailsLoaded$ = this.select('userDetailsLoaded');
  readonly userId$ = this.select('userId');

  constructor(protected localStorageService: LocalStorageService,) {
    super(localStorageService, StoreName.ServiceWorkerCommonStore, {
      backendUrl: ['', DataType.String],
      contextMenuActions: [[], DataType.Object],
      copilotNotificationConfigurations: [[], DataType.Object],
      copilotScripts: [[], DataType.Object],
      isExtensionPopupOpened: [false, DataType.Boolean],
      isSignedIn: [false, DataType.Boolean],
      lastSignInTime: [null, DataType.Number],
      latestDashboardMessage: [null, DataType.Object],
      profileEmail: ['', DataType.String],
      studentId: [null, DataType.String],
      userDetails: [null, DataType.Object],
      userDetailsLoaded: [false, DataType.Boolean],
      userId: [null, DataType.String],
    });
  }

  async setBackendUrl(data: string): Promise<void> {
    await this.setValue('backendUrl', data);
  }

  async setContextMenuActions(data: ContextMenuActionData[]): Promise<void> {
    await this.setValue('contextMenuActions', data);
  }

  async setCopilotNotificationConfigurations(data: CopilotNotificationConfiguration[]): Promise<void> {
    await this.setValue('copilotNotificationConfigurations', data);
  }

  async setCopilotScripts(data: CopilotScript[]): Promise<void> {
    await this.setValue('copilotScripts', data);
  }

  async setIsExtensionPopupOpened(data: boolean): Promise<void> {
    await this.setValue('isExtensionPopupOpened', data);
  }

  async setIsSignedIn(data: boolean): Promise<void> {
    await this.setValue('isSignedIn', data);
  }

  async setLastSignInTime(data: number | null): Promise<void> {
    await this.setValue('lastSignInTime', data);
  }
  
  async setLatestDashboardMessage(data: DashboardMessage): Promise<void> {
    await this.setValue('latestDashboardMessage', data);
  }

  async setProfileEmail(data: string): Promise<void> {
    await this.setValue('profileEmail', data);
  }

  async setStudentId(data: string): Promise<void> {
    await this.setValue('studentId', data);
  }

  async setUserDetails(data: UserDetails): Promise<void> {
    await this.setValue('userDetails', data);
  }

  async setUserDetailsLoaded(data: boolean): Promise<void> {
    await this.setValue('userDetailsLoaded', data);
  }

  async setUserId(data: string): Promise<void> {
    await this.setValue('userId', data);
  }
}
